// Branded site chrome for Godishni-Otcheti.
//
// Layout structure (per the design bundle's ui_kits/website):
//   ┌── 980px page container (centered) ─────────────────────┐
//   │  SiteHeader      — olive-green, wordmark + tagline      │
//   │  TopNav          — lighter olive, uppercase, underline  │
//   │  <main>          — page content (route Outlet)          │
//   │  BottomNav       — repeats TopNav (classic UX pattern)  │
//   │  Footer          — copyright + legal links              │
//   └─────────────────────────────────────────────────────────┘
//
// Brand rules enforced here:
//   - Fixed 980px page width; no fluid SaaS layouts.
//   - Green is chrome only (bg-primary header + bg-green-600 nav strip) —
//     never leaks into body. We use the brand utility (bg-green-600) for the
//     nav, NOT bg-accent: the shadcn `--accent` token is a pale "subtle
//     interactive surface" (#eef3e5) and doesn't match the brand's nav strip.
//   - Nav items ALL-CAPS Bulgarian; active state = dark wash + underline bar.
//   - No icons in chrome (brand forbids decorative icons).

import { cn } from '@podium/platform/ui'
import { Link } from '@tanstack/react-router'

import type { ReactNode } from 'react'

type NavItem = readonly [href: string, label: string]

const NAV_ITEMS: readonly NavItem[] = [
  ['/', 'Начало'],
  ['/publish', 'Публикуване'],
  ['/reports', 'Публикувани отчети'],
  ['/law', 'Нормативна уредба'],
  ['/prices', 'Цени'],
  ['/discounts', 'Отстъпки'],
  ['/contact', 'Контакти'],
  ['/logout', 'Изход'],
] as const

export function GfoShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-[980px]">
        <SiteHeader />
        <SiteNav />
        <main>{children}</main>
        <SiteNav variant="footer" />
        <SiteFooter />
      </div>
    </div>
  )
}

function SiteHeader() {
  return (
    <header className="bg-primary px-[30px] pb-6 pt-7 text-primary-foreground">
      <h1 className="m-0 text-[32px] font-semibold leading-[1.1] tracking-[-0.02em]">
        <span className="font-normal text-white/55">www.</span>
        Godishni-Otcheti
        <span className="font-normal text-white/55">.com</span>
      </h1>
      <div className="mt-2.5 text-xs tracking-[0.02em] text-white/80">
        Специализирано интернет издание за публикуване на финансови отчети
      </div>
    </header>
  )
}

function SiteNav({ variant = 'top' }: { variant?: 'top' | 'footer' }) {
  return (
    <nav
      className={cn(
        'flex items-stretch bg-green-600 px-[22px]',
        variant === 'top' ? 'border-t border-black/10' : 'justify-center',
      )}
    >
      {NAV_ITEMS.map(([href, label]) => (
        <Link
          key={href}
          to={href}
          activeOptions={{ exact: href === '/' }}
          className={cn(
            'relative inline-flex items-center px-3.5 py-[11px]',
            'text-xs font-semibold uppercase tracking-[0.02em] text-white',
            'no-underline transition-colors hover:bg-white/10',
            variant === 'top' &&
              'aria-[current=page]:bg-black/10 aria-[current=page]:after:absolute aria-[current=page]:after:bottom-0 aria-[current=page]:after:left-3.5 aria-[current=page]:after:right-3.5 aria-[current=page]:after:h-0.5 aria-[current=page]:after:bg-white',
          )}
        >
          {label}
        </Link>
      ))}
    </nav>
  )
}

function SiteFooter() {
  return (
    <div className="px-0 py-[14px] text-center text-xs text-muted-foreground">
      Copyright © 2008–2026 www.Godishni-Otcheti.com.
      <br />
      <a href="/terms">Общи условия</a> {' | '}
      <a href="/cookies">Политика за бисквитките</a> {' | '}
      <a href="/privacy">Политика за поверителност</a>
    </div>
  )
}
